import { FC, Ref } from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import {
  Breadcrumb,
  FullBoatSearchDetail,
  OptimizedBoatSearchDetail,
} from 'src/common/types/api/v4/boats/search';
import getBreadcrumbs from 'src/common/utils/getBreadcrumbs';
import { ListingCardLocation } from 'src/common/types/common/ListingCard';
import { InquirySentIndicator } from 'src/common/types/api/v4/trips';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import Carousel from 'src/common/components/dockyard/Carousel';
import ListingCard, { ListingCardGtagEvent, ListingCardVariants } from 'src/common/components/ListingCard';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import GulperImpressions from 'src/common/components/GulperImpressions';
import styles from './HighlightedListingsCarousel.module.scss';

const IMAGES_TO_LOAD = 1;

type ActiveBoat = Pick<FullBoatSearchDetail, 'id'>;

type HighlightedListingsCarouselProps = {
  listings: OptimizedBoatSearchDetail[];
  mapOpen: boolean;
  activeBoat?: ActiveBoat | null;
  activeBoatRef?: Ref<HTMLDivElement>;
  inquiryIndicators?: InquirySentIndicator[];
  parentBreadcrumbs?: Breadcrumb[];
  children: React.ReactNode;
  backgroundColor?: 'neutral' | 'grey';
  cardLocation?: ListingCardLocation;
  gtagEvent?: ListingCardGtagEvent;
  instantBookingAvailable?: boolean;
  variant?: ListingCardVariants;
  hideCarousel?: boolean;
  'data-gulper-section': 'Search' | 'BookNow' | 'MostPopular' | 'Broadcast';
};

const HighlightedListingsCarousel: FC<HighlightedListingsCarouselProps> = ({
  listings,
  children,
  mapOpen,
  activeBoat,
  activeBoatRef,
  inquiryIndicators = [],
  parentBreadcrumbs = [],
  backgroundColor,
  cardLocation,
  gtagEvent,
  instantBookingAvailable,
  variant,
  hideCarousel = false,
  ...dataAttributes
}) => {
  const [t] = useTranslation(['search-page', 'listing-page']);
  const { isMobileNativeWebView } = useMobileNativeWebView();
  if (!listings || hideCarousel) return null;

  return (
    <div
      className={getClassNames(styles, 'wrapper', classNames(backgroundColor))}
      {...dataAttributes}
    >
      <CarouselProvider>
        {(selectedIndex: number) => (
          <div className={getClassNames(styles, 'root', classNames({ mapOpen }))}>
            <div className={styles.header}>
              <div className={styles.copyWrapper}>
                {children}
              </div>
              <div className={styles.desktopButtons}>
                <CarouselButtons size="small" spacing="together" variant="primary-white" shadow />
              </div>
            </div>

            <Carousel>
              {listings.map((listing, index) => {
                const isActive = activeBoat?.id === listing.id;
                const hasActiveInquiry = inquiryIndicators.some(
                  ({ listing_id }) => listing_id === listing.id,
                );
                if (
                  !listing.instabook_enabled
                  && (listing.has_available_instabook || instantBookingAvailable)
                ) return null;

                return (
                  <ListingCard
                    key={listing.id}
                    asCarouselItem
                    listingId={listing.id}
                    cardRef={isActive ? activeBoatRef : null}
                    isActive={isActive}
                    listingUrl={listing.listing_url}
                    imageSrc={listing.photo_url}
                    capacity={listing.capacity ? t('listing-page:guestCapacity', { count: listing.capacity }) : ''}
                    headline={listing.headline}
                    rating={listing.review_average ? parseFloat(listing.review_average) : undefined}
                    isSuperOwner={listing.user_details.is_super_owner}
                    breadcrumbs={getBreadcrumbs(listing.breadcrumbs, parentBreadcrumbs)}
                    category={listing.cat_loc_breadcrumb}
                    awards={listing.user_details?.awards}
                    loading={index < IMAGES_TO_LOAD ? 'eager' : 'lazy'}
                    fetchpriority={index === 0 ? 'high' : null}
                    instantBookingAvailable={
                      listing.has_available_instabook || instantBookingAvailable
                    }
                    variant={variant}
                    inquirySent={hasActiveInquiry}
                    searchPricing={listing.search_price_nested}
                    cardLocation={cardLocation}
                    gtagEvent={gtagEvent}
                    withFavorite={!isMobileNativeWebView}
                  />
                );
              })}
            </Carousel>
            <GulperImpressions data={listings} />

            <div className={styles.dots}>
              {listings.map((listing, index) => (
                <div
                  key={listing.id}
                  className={
                    selectedIndex === index
                      ? styles.selected
                      : styles.notSelected
                  }
                />
              ))}
            </div>
          </div>
        )}
      </CarouselProvider>
    </div>
  );
};

export default HighlightedListingsCarousel;
